import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AnimalTrendDataType } from '../../models/animal';
import { Box, Grid, Icon, IconButton } from '@material-ui/core';
import { useStyles } from './styles';
import { Skeleton } from '../../elements/ui/skeleton';
import { ApiService, CompareSearchService } from '../../hooks';
import { notifications } from '../../utils';
import {
  HealthSystemDALEnumsAnimalTrendDataType,
  HealthSystemDomainAnimalsCommandsSearchCompareAnimalsTrendDataQuery,
  HealthSystemDomainAnimalsResultAnimalsFarmSearchRequest
} from '../../api/HealthSystemApi';
import { AnimalTrendData, toAnimalTrendDataByKey } from '../../models/animal-trend-data';
import { TrendDataMulti } from '../../components';
import { useTranslation } from 'react-i18next';
import useAuthContext from '../../hooks/useAuthContext';
import { SearchCompareViewModel } from '../../models/common';

const AnimalCompareSearch: React.FC = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { state } = useAuthContext();
  const navigate = useNavigate();

  const apiService = new ApiService();
  const compareSearchService = new CompareSearchService();

  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const selectedIds =
    params
      .get('ids')
      ?.split(',')
      .filter((id) => id) ?? [];

  const [loaded, setLoaded] = React.useState(false);
  const [isBusy, setIsBusy] = React.useState(false);
  const [showPercentage, setShowPercentage] = React.useState(false);

  const [searchTrendData, setSearchTrendData] = React.useState<Record<string, AnimalTrendData>>();
  const [savedQueries, setSavedQueries] = React.useState<SearchCompareViewModel[]>([]);

  const [animalTrendDataType, setAnimalTrendDataType] = React.useState<HealthSystemDALEnumsAnimalTrendDataType>(
    AnimalTrendDataType.SlaughterhouseDataWeight
  );

  const searchTrend = async () => {
    if (selectedIds.length < 2) {
      notifications.warning(t('t-compare-search-validation-error'));
      return;
    }

    // Saved comparisons are scoped per farm, and farmId is a JWT claim only a farm token has.
    // This page is only reachable from the farm animal search, so it is there in practice - but
    // treat its absence as "nothing to compare" instead of looking up queries for no farm.
    if (!state.farmId) {
      notifications.warning(t('t-compare-search-validation-error'));
      return;
    }

    setIsBusy(true);
    setLoaded(false);

    try {
      const data = await compareSearchService.getByIdsAsync(state.farmId, selectedIds);

      // Backend expects Int64 keys — use positional indices
      const command: HealthSystemDomainAnimalsCommandsSearchCompareAnimalsTrendDataQuery = {
        animalTrendDataType,
        queries: data.reduce<Record<string, HealthSystemDomainAnimalsResultAnimalsFarmSearchRequest>>(
          (acc, item, index) => {
            acc[index.toString()] = item.query;
            return acc;
          },
          {}
        )
      };

      const result = await apiService.api.animals.animalsGetCompareSearchAnimalsTrendData(command);
      setSearchTrendData(toAnimalTrendDataByKey(result.data));
      setSavedQueries(data);
      setLoaded(true);
    } catch (e: any) {
      setLoaded(false);
      notifications.error(e?.error?.message ?? e.toString());
    } finally {
      setIsBusy(false);
    }
  };

  React.useEffect(() => {
    searchTrend();
  }, [selectedIds.join(',')]);

  React.useEffect(() => {
    if (!loaded) {
      searchTrend();
    }
  }, [loaded]);

  const goBack = () => {
    navigate('/animal');
  };

  return (
    <Grid container>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box>
          <IconButton color="primary" size="medium" aria-label={t('f-com-create-new')} onClick={() => goBack()}>
            <Icon>arrow_back</Icon>
          </IconButton>
        </Box>
        <Box>
          <IconButton color="primary" size="medium" aria-label={t('f-com-create-new')} onClick={() => setLoaded(false)}>
            <Icon>refresh</Icon>
          </IconButton>
        </Box>
      </Box>

      <Grid container className={classes.withMargin}>
        <Grid item xs={12}>
          {isBusy && (
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <Skeleton className="h-7 w-40" />
                <Skeleton className="h-7 w-7 rounded-full" />
              </div>
              <Skeleton className="h-9 w-48" />
              <div className="rounded-lg border border-gray-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200">
                  <Skeleton className="h-4 w-32" />
                </div>
                <div className="p-6">
                  <div className="flex items-end gap-3 h-62.5">
                    {[55, 75, 45, 85, 65, 90, 50, 70, 80, 60, 40, 72].map((h, i) => (
                      <Skeleton key={i} className="flex-1 rounded-t-sm rounded-b-none" style={{ height: `${h}%` }} />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
          {loaded && searchTrendData && (
            <TrendDataMulti
              showPercentage={showPercentage}
              onShowPercentageChange={setShowPercentage}
              animalTrendDataType={animalTrendDataType}
              trendData={Object.entries(searchTrendData).map(([key, value]) => ({
                name: savedQueries[parseInt(key, 10)]?.name ?? `Search ${key}`,
                color: undefined,
                data: value.trendData.map((trend) => ({
                  ...trend,
                  id: parseInt(key, 10),
                  trendDataType: animalTrendDataType,
                  color: undefined
                }))
              }))}
              slaughterhouseDataAverage={null}
              weightAverage={null}
              onAnimalTrendDataTypeChanged={(type) => {
                setAnimalTrendDataType(type);
                setLoaded(false);
              }}
            />
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default AnimalCompareSearch;
